import React, { memo, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import classNames from 'classnames';
import { ChevronDownIcon } from '@heroicons/react/20/solid';
import Select, { components } from 'react-select';
import { get, map } from 'lodash';
import {
  API_STATUS,
  SELECT_FIELD_DATA_LIMIT,
  SELECT_SEARCH_DEBOUNCE_MS,
  VALIDATION_ERROR_MESSAGES,
} from '@v7/form/src/utils/constant';
import { useGetSelectFieldDataQuery } from '@v7/form/src/store/services/updateFormBuilder';
import { parseOptions, resolveFieldTypeId, resolveParentLabel } from '@v7/form/src/utils/blockProps';
import { useDebounce } from '@v7/form/src/utils/useDebounce';
import { RootState } from '@v7/form/src/store';
import Toggle from '@v7/form/src/components/Popover/MenuPopover/ToggleSwitch';
import FieldLabel from '@v7/form/src/components/Tools/FieldLabel';
import { handleInvalidField } from '@v7/form/src/components/Tools/TriggerValidation/inputUtils';
import TriggerValidation from '@v7/form/src/components/Tools/TriggerValidation';

interface SelectFieldData {
  key: string | number;
  value: string | number;
  id: string | number;
}

interface SelectFieldDataQuery {
  frm_select_field_data: SelectFieldData[];
}

/**
 * A single dropdown choice. Covers both sources: rows mapped from
 * `frm_select_field_data` (`label`/`value`/`id`) and inline options persisted in the
 * form structure (which also carry `name`). All fields are optional because
 * `labelKey`/`primaryKey` are caller-supplied, so a consumer may key off any of them.
 */
export interface SelectOption {
  id?: string | number;
  name?: string;
  label?: string;
  value?: string | number;
}

/** Args for `getSelectFieldData`. `null` while the field is static or the menu is closed. */
interface SelectFieldDataArgs {
  id: number;
  parentId?: number;
  search: string;
  limit: number;
}

export const reactSelectDefaultStyles = {
  input: (props: any) => ({
    ...props,
    input: {
      boxShadow: 'none',
    },
  }),
  indicatorSeparator: (props: any) => ({
    ...props,
    display: 'none',
  }),
  placeholder: (props: any) => ({
    ...props,
    color: '#333940',
    opacity: 0.4,
    fontWeight: 400,
    fontSize: '14px',
  }),
  option: (props: any, option: any) => ({
    ...props,
    fontSize: '14px',
    backgroundColor: option.isSelected ? '#1d4ed8' : props.backgroundColor,
  }),
  singleValue: (props: any) => ({
    ...props,
    fontSize: '14px',
  }),
  multiValue: (props: any) => ({
    ...props,
    backgroundColor: '#DBEAFE',
    borderRadius: '4px',
    padding: '0px 8px 0px 8px',
    marginRight: '8px',
  }),
  multiValueLabel: (props: any) => ({
    ...props,
    color: '#1D4ED8',
    fontSize: '12px',
    fontWeight: '500',
  }),
  multiValueRemove: (props: any) => ({
    ...props,
    '&:hover': {
      backgroundColor: 'transparent',
      color: '#1D4ED8',
    },
    color: '#1D4ED8',
  }),
  control: (provided: any, state: any) => {
    const borderColor =
      state?.selectProps?.error || state?.selectProps?.validation?.triggerInvalid ? '#b91c1c' : '#d1d5db';
    return {
      ...provided,
      borderColor,
      borderRadius: '0.375rem',
      boxShadow: 'none !important',
      '&:hover': {
        borderColor:
          state?.selectProps?.error || state?.selectProps?.validation?.triggerInvalid ? '#b91c1c' : '#9CA3AF',
        cursor: 'pointer',
      },
      '&:focus': {
        borderColor:
          state?.selectProps?.error || state?.selectProps?.validation?.triggerInvalid ? '#b91c1c' : '#6B7280',
      },
      '&:focus-within': {
        borderColor:
          state?.selectProps?.error || state?.selectProps?.validation?.triggerInvalid ? '#b91c1c' : '#6B7280',
      },
    };
  },
};

export function DropdownIndicator(props: any) {
  return (
    <components.DropdownIndicator {...props}>
      <ChevronDownIcon className="w-6 h-6" />
    </components.DropdownIndicator>
  );
}

const SelectDropdown = (props: any) => {
  const {
    value,
    onChange,
    fieldEnableChange,
    disabled,
    readOnly,
    isLoading,
    isClearable,
    isSearchable = false,
    name = 'dropdown',
    placeholder,
    options,
    primaryKey,
    labelKey,
    emptyLabel,
    customStyles,
    isMulti = false,
    error,
    label,
    required,
    hasToggle,
    isEnabled,
    selectFieldType,
    selectParentField,
    tooltip,
    id,
  } = props;
  const formData = useSelector((state: RootState) => state?.form?.formData);
  // both props are objects in v7-authored forms and strings in modules/form-authored ones
  const parentLabel = resolveParentLabel(selectParentField);
  const parentId = parentLabel ? get(formData, `${parentLabel}.id`) : undefined;

  const fieldTypeId = resolveFieldTypeId(selectFieldType);
  const isApiDriven = fieldTypeId !== null;

  const [selectedValue, setSelectedValue] = useState(value);
  const [isToggled, setIsToggled] = useState(isEnabled || false);
  // An API-driven field owns its own options; any inline `options` prop is ignored so a
  // partially-migrated block (selectFieldType set, stale options still present) never
  // renders the stale list.
  const [selectOptions, setSelectOptions] = useState<SelectOption[]>(() =>
    isApiDriven ? [] : parseOptions<SelectOption>(options),
  );
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearch = useDebounce(searchTerm, SELECT_SEARCH_DEBOUNCE_MS);
  const [validation, setValidation] = useState({
    triggerInvalid: false,
    message: '',
  });

  const handleInvalid = (event: React.InvalidEvent<HTMLInputElement>) => {
    handleInvalidField(event, setValidation);
  };

  const noOptionsMessage = () => emptyLabel;
  const inputRef = useRef<any>(null);
  const formatOptionLabel = (option: any) => get(option, labelKey, option);
  const getOptionValue = (option: any) => get(option, primaryKey, option);
  const controlStyles = (provided: any, state: any) =>
    reactSelectDefaultStyles.control(provided, {
      ...state,
      selectProps: {
        ...state.selectProps,
        error,
        validation,
      },
    });

  const updateValue = (value: any) => {
    onChange?.(value);
    setSelectedValue(value);
    setValidation((prev) => ({
      ...prev,
      triggerInvalid: value ? false : true,
    }));
  };

  /**
   * Query args derived from the open menu, the resolved field type, the parent's
   * selection and the debounced search term. `null` means "don't query" — the field is
   * static, the menu is closed, or a configured parent has no value yet.
   */
  const apiArgs = useMemo<SelectFieldDataArgs | null>(() => {
    if (!isApiDriven || !isMenuOpen) return null;
    if (parentLabel && !parentId) return null;
    return {
      id: fieldTypeId,
      ...(parentId && { parentId: Number(parentId) }),
      search: debouncedSearch,
      limit: SELECT_FIELD_DATA_LIMIT,
    };
  }, [isApiDriven, isMenuOpen, fieldTypeId, parentLabel, parentId, debouncedSearch]);

  const { data: selectFieldData, status } = useGetSelectFieldDataQuery(apiArgs, { skip: !apiArgs }) as {
    data: SelectFieldDataQuery;
    status: string;
  };

  useEffect(() => {
    if (status === API_STATUS.SUCCESS && selectFieldData) {
      const fetchedOptions = map(
        get(selectFieldData, 'frm_select_field_data', []),
        (item: SelectFieldData): SelectOption => ({
          label: String(item?.value ?? ''),
          value: item?.key,
          id: item?.id,
        }),
      );
      setSelectOptions(fetchedOptions);
    }
  }, [status, selectFieldData]);

  // A parent change invalidates the child: clear its value, and for API-driven children
  // drop the previously fetched options so the old parent's rows never linger.
  //
  // `parentId` is deliberately the only dependency. `updateValue` is redefined on every
  // render (it closes over the `onChange` prop, which arrives as a fresh arrow from
  // renderHelpers), so listing it would re-run this effect on every render and reset the
  // field continuously — the child could never hold a value. `parentLabel` and
  // `isApiDriven` are read as guards only; both derive from props that cannot change
  // without `parentId` also being re-evaluated.
  useEffect(() => {
    if (!parentLabel) return;
    updateValue(null);
    if (isApiDriven) setSelectOptions([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- must fire only when the parent's selected value changes; see comment above
  }, [parentId]);

  useEffect(() => {
    if (isApiDriven) return;
    // guard on `options`, not on parsed length — `[]` is truthy and must still clear the list
    if (options) setSelectOptions(parseOptions<SelectOption>(options));
  }, [options, isApiDriven]);

  useEffect(() => {
    setSelectedValue(value);
  }, [value]);

  useLayoutEffect(() => {
    setIsToggled(isEnabled);
  }, [isEnabled]);

  useEffect(() => {
    if (required && !selectedValue?.value) inputRef.current?.setCustomValidity(VALIDATION_ERROR_MESSAGES.REQUIRED);
    else inputRef.current?.setCustomValidity('');
    setValidation((prev) => ({
      ...prev,
      message: inputRef?.current?.validationMessage,
    }));
    // `required` is a plain prop, so including it is safe and correct: toggling it must
    // recompute the field's custom validity.
  }, [selectedValue, required]);

  const onMenuOpen = () => setIsMenuOpen(true);

  const onMenuClose = () => {
    setIsMenuOpen(false);
    setSearchTerm('');
  };

  /**
   * Mirrors react-select's own input text into state so the debounced value can
   * drive the query. `inputValue` stays uncontrolled — react-select owns the
   * text; we only track it.
   */
  const onInputChange = (next: string, meta: { action: string }) => {
    if (meta.action === 'input-change') setSearchTerm(next);
    else if (meta.action === 'menu-close' || meta.action === 'set-value') setSearchTerm('');
  };

  return (
    <div className="w-full">
      <input
        ref={inputRef}
        name={name}
        value={selectedValue}
        defaultValue={value || ''}
        id={id}
        className="absolute opacity-0 pointer-events-none"
        role="button"
        onInvalid={handleInvalid}
      />
      <div className="text-sm relative flex-col" data-testid="select_button">
        {label && (
          <div className="flex justify-between items-center">
            <FieldLabel name={name} value={label} required={required} tooltip={tooltip} disabled={disabled}>
              {label}
            </FieldLabel>
            {hasToggle && (
              <Toggle
                checked={isEnabled}
                onChange={(e: boolean) => {
                  setIsToggled(e);
                  fieldEnableChange && fieldEnableChange(e);
                }}
              />
            )}
          </div>
        )}
        {(!hasToggle || (hasToggle && isToggled)) && (
          <div className={classNames('max-w-[25rem]', disabled && 'opacity-50')}>
            <Select
              data-testid="select_button"
              value={selectedValue}
              onChange={updateValue}
              isDisabled={disabled || readOnly}
              isLoading={status === API_STATUS.LOADING || isLoading}
              isClearable={isClearable}
              isSearchable={isApiDriven || isSearchable}
              filterOption={isApiDriven ? null : undefined}
              onInputChange={isApiDriven ? onInputChange : undefined}
              name={name}
              placeholder={placeholder}
              options={selectOptions}
              getOptionValue={getOptionValue}
              getOptionLabel={formatOptionLabel}
              formatOptionLabel={formatOptionLabel}
              noOptionsMessage={noOptionsMessage}
              styles={{
                ...reactSelectDefaultStyles,
                ...customStyles,
                control: controlStyles,
              }}
              components={{
                DropdownIndicator,
              }}
              isMulti={isMulti}
              menuPlacement={'auto'}
              onMenuOpen={onMenuOpen}
              onMenuClose={onMenuClose}
            />
            {error && <p className="text-red-700 mt-1 text-xs">{error}</p>}
          </div>
        )}
      </div>
      <TriggerValidation
        validationTrigger={validation?.triggerInvalid}
        validationMessage={validation?.message}
        name={name}
        alterClassName="mt-2"
      />
    </div>
  );
};

export default memo(SelectDropdown);
